import React, { useState } from 'react';
import PhoneInput from '../components/PhoneInput';
import { Check, XCircle } from 'lucide-react';

const PhonePOC = () => {
  const [phone1, setPhone1] = useState(null);
  const [phone2, setPhone2] = useState(null);
  const [phone3, setPhone3] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  const testValidation = async (phoneData) => {
    if (!phoneData) {
      alert('Please enter a phone number first');
      return;
    }

    setTesting(true);
    try {
      const response = await fetch(`${backendUrl}/api/poc/phone/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(phoneData)
      });

      const result = await response.json();
      setValidationResult(result);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Phone Number POC</h1>
          <p className="text-gray-600 mb-8">Testing standardized phone number input and validation</p>

          {/* Test Inputs */}
          <div className="space-y-6 mb-8">
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">Test 1: US Phone Number</h2>
              <PhoneInput
                value={phone1}
                onChange={setPhone1}
                label="Client Direct Phone"
                required={true}
                placeholder="2025551234"
              />
              {phone1 && (
                <div className="mt-4">
                  <button
                    onClick={() => testValidation(phone1)}
                    disabled={testing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {testing ? 'Validating...' : 'Validate Phone'}
                  </button>
                </div>
              )}
            </div>

            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold mb-4">Test 2: Mobile Phone</h2>
              <PhoneInput
                value={phone2}
                onChange={setPhone2}
                label="Mobile Phone"
                placeholder="Enter mobile number"
              />
              {phone2 && (
                <div className="mt-4">
                  <button
                    onClick={() => testValidation(phone2)}
                    disabled={testing}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    Validate Mobile
                  </button>
                </div>
              )}
            </div>

            <div className="pb-6">
              <h2 className="text-xl font-semibold mb-4">Test 3: International Phone (Try different countries)</h2>
              <PhoneInput
                value={phone3}
                onChange={setPhone3}
                label="Other Phone"
                placeholder="Enter international number"
              />
              {phone3 && (
                <div className="mt-4">
                  <button
                    onClick={() => testValidation(phone3)}
                    disabled={testing}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    Validate International
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Validation Result */}
          {validationResult && (
            <div className={`border-2 rounded-lg p-6 ${
              validationResult.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
            }`}>
              <div className="flex items-center gap-2 mb-4">
                {validationResult.success ? (
                  <Check className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
                <h3 className="text-lg font-semibold">
                  {validationResult.success ? 'Valid Phone Number ✓' : 'Invalid Phone Number ✗'}
                </h3>
              </div>

              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Input:</span>
                    <span className="ml-2">{validationResult.input.countryCode} {validationResult.input.number}</span>
                  </div>
                  {validationResult.validation.formatted && (
                    <div>
                      <span className="font-medium">Formatted:</span>
                      <span className="ml-2 text-green-700">{validationResult.validation.formatted}</span>
                    </div>
                  )}
                </div>

                {validationResult.validation.country && (
                  <div>
                    <span className="font-medium">Country:</span>
                    <span className="ml-2">{validationResult.validation.country}</span>
                  </div>
                )}

                {validationResult.validation.type && (
                  <div>
                    <span className="font-medium">Type:</span>
                    <span className="ml-2 capitalize">{validationResult.validation.type}</span>
                  </div>
                )}

                {validationResult.validation.error && (
                  <div className="text-red-600">
                    <span className="font-medium">Error:</span>
                    <span className="ml-2">{validationResult.validation.error}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 p-3 bg-gray-100 rounded text-xs font-mono">
                <div className="font-semibold mb-2">JSON Format (for database):</div>
                <pre>{JSON.stringify(validationResult.input, null, 2)}</pre>
              </div>
            </div>
          )}

          {/* Current Values Display */}
          <div className="mt-8 p-4 bg-gray-100 rounded-lg">
            <h3 className="font-semibold mb-3">Current Form Values:</h3>
            <div className="space-y-2 text-sm font-mono">
              <div>Phone 1: {phone1 ? JSON.stringify(phone1) : 'null'}</div>
              <div>Phone 2: {phone2 ? JSON.stringify(phone2) : 'null'}</div>
              <div>Phone 3: {phone3 ? JSON.stringify(phone3) : 'null'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhonePOC;
